import React, { useEffect, useState } from "react";
import useAxios from "../../../utils/useAxios";
import { ParticipantPickerPanel } from "../../../components/CustomModel/ParticipantPickerPanel";
import { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import { useIsWideViewport } from "../../../hooks/useIsWideViewport";
import { normalizeTeamPlayers } from "../../../utils/normalizeTeamPlayers";

const TournamentGroupsSelectPlayerModal = ({
                                               isOpen,
                                               onClose,
                                               onSuccess,
                                               tournamentGrade,
                                               rating,
                                               onAddToCell,
                                               stageId,
                                               groupId,
                                               participants,
                                               occupiedParticipants = [],
                                               position,
                                               setLoadingCells,
                                               pickedCandidate,
                                               onPickCandidate
                                           }) => {
    const api = useAxios();
    const isWide = useIsWideViewport();

    const [query, setQuery] = useState("");
    // This component stays mounted across opens/closes (the parent just
    // toggles `isOpen`, see the early return below), so the search input's
    // own state would otherwise carry a previous search over into the next
    // open - clear it on every open, not close, so it doesn't flash empty
    // while the panel is still closing.
    useEffect(() => {
        if (isOpen) setQuery("");
    }, [isOpen]);
    // A Set of in-flight candidate keys, not one global boolean - on wide
    // viewports the panel stays open across multiple picks (see onClose
    // below), so a second pick needs to be submittable while the first is
    // still in flight instead of the whole list locking up until it resolves.
    const [submittingIds, setSubmittingIds] = useState(() => new Set());

    const getParticipantKey = (participant) =>
        String(tournamentGrade === 1 ? (participant.player_id || participant.id) : (participant.team_id || participant.id));

    // `/group-stage/{id}/participants/` names the team's own rating `rating`
    // (confirmed live via the matching /draw-stage/.../participants/ - same
    // endpoint family) - NOT `team_rating`, that's a /teams/-endpoint-only
    // name (TournamentPlayers.js/PlayersGridView.js both read it from
    // there). Checking `team_rating` first/alone here always missed,
    // falling through to summing `players[].rating` - which this endpoint's
    // own nested players don't carry either (id/first_name/last_name only),
    // so every pair/team showed 0 regardless of its real rating.
    //
    // Newly created team/pair records can still come back from the backend
    // with their OWN rating at 0 until a later recalculation fills it in
    // (same root cause as backend-tasks/2026-09-12-copied-tournament-team-rating-zero.md),
    // while each member player's own `rating` is already correct straight
    // away - summing those stays as the last-resort fallback for that case.
    const getDisplayRating = (participant) => {
        if (tournamentGrade === 1) return participant?.rating || 0;
        // One rating per slot: a pair carrying a third member (duplicate
        // `sorting`, see src/utils/normalizeTeamPlayers.js) summed three
        // ratings and showed a pair as stronger than it is.
        return participant?.team_rating ?? participant?.rating ??
            normalizeTeamPlayers(participant?.players, tournamentGrade === 2 ? 2 : 0)
                .reduce((acc, p) => acc + (p?.rating || 0), 0);
    };

    // No specific slot targeted (either never had one, or the previous
    // target was just filled and cleared by the parent's onSuccess - see
    // TournamentGroups.js) - a candidate click here can't post anywhere
    // yet, so it just "picks" them instead: highlighted in this list,
    // "Drop here" on every empty cell on screen, until a cell is tapped.
    const hasTarget = groupId != null && position != null;

    const handleSelect = async (participant) => {
        if (!hasTarget) {
            onPickCandidate?.(participant);
            return;
        }

        const key = getParticipantKey(participant);
        if (submittingIds.has(key)) return;

        setSubmittingIds(prev => new Set(prev).add(key));
        const cellKey = `${groupId}_${position}`;
        setLoadingCells?.(prev => ({ ...prev, [cellKey]: true }));

        try {
            const payload = tournamentGrade === 1
                ? { position, player_id: participant.player_id || participant.id }
                : { position, team_id: participant.team_id || participant.id };

            await api.post(
                `/api/v1.0/group-stage/${stageId}/groups/${groupId}/players/`,
                payload
            );

            onAddToCell(groupId, position, participant);
            onSuccess?.(participant);
            // ParticipantPickerPanel is a plain blocking modal on every
            // viewport now (reverted off its old wide-only docked/non-
            // blocking mode) - closing after a successful add is the only
            // feedback that anything happened at all, same as it always was
            // on narrow.
            onClose();
            } catch (e) {
            console.error("Failed to add participant:", e);
        } finally {
            setSubmittingIds(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            setLoadingCells?.(prev => {
                const next = { ...prev };
                delete next[cellKey];
                return next;
            });
        }
    };

    if (!isOpen) return null;

    const isAlreadyOccupied = (participant) => {
        if (!occupiedParticipants?.length) return false;
        return occupiedParticipants.some(occupied => {
            if (tournamentGrade === 1) {
                const pId1 = participant.player_id || participant.id;
                const pId2 = occupied.player_id || occupied.id;
                return pId1 && pId2 && String(pId1) === String(pId2);
            }
            if (tournamentGrade === 2) {
                const teamId1 = participant.team_id || participant.id;
                const teamId2 = occupied.team_id || occupied.id;
                if (teamId1 && teamId2 && String(teamId1) === String(teamId2)) return true;

                // Check player IDs inside doubles pairs
                const p1List = (participant.players || []).map(p => p.id || p.player_id).filter(Boolean);
                const p2List = (occupied.players || []).map(p => p.id || p.player_id).filter(Boolean);
                return p1List.some(id => p2List.includes(id));
            }
            const tId1 = participant.team_id || participant.id;
            const tId2 = occupied.team_id || occupied.id;
            return tId1 && tId2 && String(tId1) === String(tId2);
        });
    };

    const filterParticipant = (participant) => {
        const q = query.trim().toLowerCase();

        if (tournamentGrade === 1) {
            return (
                (participant?.first_name?.toLowerCase() || "").includes(q) ||
                (participant?.last_name?.toLowerCase() || "").includes(q)
            );
        }

        // team search
        return (
            (participant?.team_name?.toLowerCase() || participant?.name?.toLowerCase() || "").includes(q) ||
            participant?.players?.some(player =>
                (player?.first_name?.toLowerCase() || "").includes(q) ||
                (player?.last_name?.toLowerCase() || "").includes(q)
            )
        );
    };

    const getFilteredParticipants = () => {
        if (!participants?.length) return [];
        // 1. Deduplicate participants pool by unique ID
        const seen = new Set();
        const uniqueParticipants = [];
        for (const p of participants) {
            const uid = tournamentGrade === 1
                ? (p.player_id || p.id)
                : (p.team_id || p.id);
            const key = uid ? String(uid) : JSON.stringify(p);
            if (!seen.has(key)) {
                seen.add(key);
                uniqueParticipants.push(p);
            }
        }

        // 2. Filter out already occupied in groups & search query - an
        // in-flight pick stays in the list (dimmed, see isSubmitting below)
        // instead of disappearing outright, so the tap has visible feedback.
        const q = query.trim().toLowerCase();
        return uniqueParticipants
            .filter(p => !isAlreadyOccupied(p))
            .filter(q ? filterParticipant : () => true);
    };

    const filteredParticipants = getFilteredParticipants();

    // Passed to ParticipantPickerPanel's `header` prop (see that component
    // and ScheduleSelectMatchModal.js/GridSelectPlayerModal.js, which this
    // mirrors) so this title/search block lives in the docked panel's own
    // fixed top zone next to the close X - same header/padding treatment
    // as the schedule and grids pickers, instead of scrolling away with the
    // participant list underneath it.
    const header = (
        <div className="modal-sticky-header">
            <h2>
                <FeatherIcon icon="user"/> {t("Choose participant")}
            </h2>
            <div className="form-group" style={{ position: "relative" }}>
                <FeatherIcon icon="search" size={16} className="search-icon" />
                <input
                    type="text"
                    className="search"
                    placeholder={
                        tournamentGrade === 3
                            ? t("Search participants by team name")
                            : t("Search participants by first name, last name")
                    }
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ paddingLeft: "30px" }}
                />
            </div>
        </div>
    );

    return (
        <ParticipantPickerPanel isOpen={isOpen} onClose={onClose} icon="user" centered header={header}>
            <form className="modal-form">
                <ul className="user-search-results players">
                    {filteredParticipants.map((player, index) => {
                        const isThisPicked = pickedCandidate && getParticipantKey(pickedCandidate) === getParticipantKey(player);
                        // Dimmed in place (not removed from the list) while
                        // the add request for this exact participant is in
                        // flight - a full-screen narrow modal has nothing
                        // else on screen to show the tap registered, so the
                        // row vanishing outright (the old behavior) read as
                        // "nothing happened" for however long the request
                        // took.
                        const isSubmitting = submittingIds.has(getParticipantKey(player));
                        return (
                        <li key={index}
                            onClick={() => handleSelect(player)}
                            style={{ display: "grid", gridTemplateColumns: rating ? "40px 1fr" : "1fr", cursor: isSubmitting ? "default" : "pointer", opacity: (isThisPicked || isSubmitting) ? 0.5 : 1, pointerEvents: isSubmitting ? "none" : "auto" }}
                        >
                            {
                                rating &&
                                <div>
                                    <div className="tournament-players" style={{ color: "#595664"}}>
                                        { getDisplayRating(player) }
                                    </div>
                                </div>
                            }
                            <div>
                                {
                                    tournamentGrade === 1 ? (
                                        <>
                                            {player?.first_name && <>{player.first_name[0]}. </>}<span>{player?.last_name}</span>
                                        </>
                                    ) : tournamentGrade === 2 ? (
                                        <>
                                            {player?.players?.[0]?.first_name && ` ${player.players[0].first_name}. `}<span>{player?.players?.[0]?.last_name}</span><br />
                                            {player?.players?.[1]?.first_name && ` ${player.players[1].first_name}. `}<span>{player?.players?.[1]?.last_name}</span>
                                        </>
                                    ) : (
                                        <>
                                            {player?.name || `${t("Team")} ${player?.number}`}
                                        </>
                                    )
                                }
                            </div>
                        </li>
                        );
                    })}
                </ul>
                
            </form>
        </ParticipantPickerPanel>
    );
};

export default TournamentGroupsSelectPlayerModal;
